import type { Theme } from "@mui/material";

/** Secondary text in the rows: quieter than a value, still AA on the chip's grey fill. */
const quietTextColor = (t: Theme) =>
  t.palette.mode === "light" ? t.palette.grey[700] : t.palette.grey[400];

/**
 * Geometry and state colour for the waste line matching rows.
 *
 * Two columns: the e-Manifest lines and the vendor lines. Both lists stay
 * complete, and a vendor line sits level with the e-Manifest line it was
 * matched to, so the pair being compared ends up on one row without either
 * source being hidden behind a menu. A vendor line nothing claimed keeps a row
 * of its own at the point in the vendor's numbering where it falls, which is
 * what makes a dropped line legible: the gap appears next to the lines it is
 * between.
 *
 * A vendor line is dragged to the e-Manifest line it belongs to, so the second
 * column is both a card and a place to drop one. The menu on the e-Manifest
 * line does the same thing from the keyboard and stays the equal path: the
 * drag states here are all appearance.
 *
 * No height cap and no scroller of its own. The shared Dialog scrolls its
 * content already, and a second scrolling box inside it puts two scrollbars
 * side by side with the rows split between them.
 *
 * Written as container selectors against the data attributes the rows carry,
 * rather than an sx prop on every element, so the rows stay readable as markup.
 *
 * Its own module rather than beside the dialog so that file only exports
 * components: mixing a constant in defeats Vite's Fast Refresh and forces a
 * full reload on every edit.
 */
export const pairingListSx = {
  border: 1,
  borderColor: "divider",
  borderRadius: 1,
  // A tinted ground so each line reads as a card sitting on it.
  bgcolor: (t: Theme) =>
    t.palette.mode === "light" ? t.palette.grey[100] : t.palette.background.default,

  // Card, connector, card. The connector column is what joins a pair.
  "& [data-row]": {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) 40px minmax(0, 1fr)",
    px: 1.5,
    py: 0.75,
    alignItems: "stretch",
  },

  // Joins the two cards of a pair, in the colour of its state. Dashed where
  // one side has nothing, so a gap reads as a broken connection.
  "& [data-link]": {
    alignSelf: "center",
    borderTop: 3,
    borderTopStyle: "solid",
    borderColor: "divider",
  },
  "& [data-link='agree']": { borderColor: "success.main" },
  "& [data-link='differ']": { borderColor: "warning.main" },
  "& [data-link='gap']": { borderTopStyle: "dashed", borderTopWidth: 2 },

  // The reading preference, above the columns it changes.
  // Detail turns four values into every field the source recorded, so the rows
  // need room to breathe at the same time.
  "&[data-detailed='detailed'] [data-row]": { py: 1.25 },

  "& [data-toolbar]": {
    display: "flex",
    alignItems: "center",
    px: 1.5,
    pt: 0.5,
    borderBottom: 1,
    borderColor: "divider",
  },

  "& [data-head]": {
    color: quietTextColor,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    fontWeight: 600,
    pt: 1,
    pb: 0.5,
  },

  // A line's label with its control on the right: the e-Manifest line's menu
  // button, or the vendor card's drag handle.
  "& [data-line-head]": {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 0.5,
  },

  // A line as one of the two sources holds it. A bar down its left edge
  // carries the state, so the row reads as two columns of values rather than
  // four nested boxes.
  "& [data-cell]": {
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: 0.25,
    bgcolor: "background.paper",
    border: 1,
    borderColor: "divider",
    borderLeftWidth: 3,
    borderRadius: 1,
    boxShadow: 1,
    px: 1.25,
    py: 1,
  },
  "& [data-cell='agree']": { borderLeftColor: "success.main" },
  "& [data-cell='differ']": { borderLeftColor: "warning.main" },
  "& [data-cell='gap']": { borderLeftStyle: "dashed" },
  // The half of a row where a source has nothing. A dashed outline with no
  // fill rather than nothing at all: a gap is a finding in its own right.
  "& [data-cell='empty']": {
    bgcolor: "transparent",
    boxShadow: "none",
    borderStyle: "dashed",
    borderLeftWidth: 1,
    justifyContent: "center",
    color: quietTextColor,
  },

  // The menu button that opens the same choice a drag makes. Pulled in by its
  // own margin so a small IconButton does not make the row taller.
  "& [data-pick]": { m: -0.5 },

  // The vendor half of a row, as a place a card can be dropped. A wrapper
  // rather than the card itself, because the slot has to stay a target while
  // it is empty, which is exactly when there is no card to hang the handlers
  // on.
  "& [data-slot]": { display: "flex", minWidth: 0 },
  "& [data-slot] > *": { flex: 1, minWidth: 0 },
  // Marked on the card rather than behind it: the card carries its own fill,
  // so a background on the wrapper is hidden by whatever is sitting in it.
  "& [data-slot='over'] [data-cell]": {
    borderColor: "primary.main",
    borderStyle: "solid",
    boxShadow: (t: Theme) => `0 0 0 1px ${t.palette.primary.main}`,
  },

  // A vendor line in the hand. Same grab cursor whether it is in a slot or in
  // a row of its own, so the two read as the same object.
  "& [data-drag]": { cursor: "grab" },
  "& [data-drag='dragging']": { opacity: 0.55 },
  "& [data-grip]": { fontSize: "1.25rem", color: quietTextColor },

  // Where a vendor line goes to pair with nothing. Always present, including
  // when nothing is unpaired, because a drop target that appears only once it
  // is already needed is one nobody can aim at.
  "& [data-tray]": {
    display: "flex",
    alignItems: "center",
    gap: 1,
    flexWrap: "wrap",
    mx: 1.5,
    mt: 0.5,
    mb: 1.5,
    px: 1.25,
    py: 1,
    border: 1,
    borderStyle: "dashed",
    borderColor: "divider",
    borderRadius: 1,
  },
  "& [data-tray='over']": {
    borderStyle: "solid",
    borderColor: "primary.main",
    bgcolor: "action.hover",
  },

  "& [data-line]": { fontWeight: 600, fontVariantNumeric: "tabular-nums" },

  "& [data-chips]": { display: "flex", flexWrap: "wrap", gap: 0.5 },

  // One value the two sources hold. Marked where they disagree about it, so the
  // reader looks at the field rather than at a verdict they then have to place.
  "& [data-chip]": {
    display: "inline-flex",
    alignItems: "baseline",
    gap: 0.5,
    fontVariantNumeric: "tabular-nums",
    fontSize: "0.75rem",
    lineHeight: 1.6,
    px: 0.75,
    borderRadius: 0.5,
    bgcolor: "action.hover",
    color: "text.primary",
    // Capped at the cell and allowed to wrap: a line can carry a dozen waste
    // codes, and one unbroken chip of them runs out of its column.
    maxWidth: "100%",
    minWidth: 0,
  },
  "& [data-chip-value]": { minWidth: 0, overflowWrap: "anywhere" },
  // Which box the value came from. Quieter than the value, because the reader
  // is comparing numbers and reads the label only when they need to place one.
  "& [data-chip-label]": {
    flexShrink: 0,
    whiteSpace: "nowrap",
    fontSize: "0.6875rem",
    letterSpacing: "0.04em",
    color: quietTextColor,
    fontWeight: 400,
  },
  // Outlined rather than coloured text: orange text on the chip's grey fails
  // AA contrast at this size, and an outline does not depend on colour alone.
  "& [data-chip='differs']": {
    boxShadow: (t: Theme) => `inset 0 0 0 1px ${t.palette.warning.dark}`,
    fontWeight: 600,
  },

  // Every field the source recorded, for the reader who turned detail on.
  // Values wrap rather than truncate: the shipping description is the longest
  // field on a manifest and the reason to turn it on at all.
  "& [data-fields]": {
    display: "grid",
    gridTemplateColumns: "minmax(0, auto) minmax(0, 1fr)",
    columnGap: 1.25,
    alignItems: "baseline",
  },
  "& [data-field-label]": { color: quietTextColor, whiteSpace: "nowrap", lineHeight: 1.6 },
  "& [data-field-value]": {
    fontSize: "0.8125rem",
    fontVariantNumeric: "tabular-nums",
    overflowWrap: "anywhere",
    lineHeight: 1.6,
  },
  "& [data-field-value='differs']": {
    fontWeight: 600,
    textDecoration: "underline",
    textDecorationColor: (t: Theme) => t.palette.warning.dark,
    textDecorationThickness: "2px",
    textUnderlineOffset: "3px",
  },

  "& [data-desc]": {
    fontSize: "0.75rem",
    color: quietTextColor,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
} as const;

/** The colour key under the rows, so the bars do not have to be guessed at. */
export const pairingLegendSx = {
  display: "flex",
  flexWrap: "wrap",
  gap: 2,
  "& [data-key]": { display: "flex", alignItems: "center", gap: 0.75 },
  // The same bar the rows carry, at the size it appears there.
  "& [data-swatch]": {
    width: 0,
    height: 14,
    borderLeft: 3,
    borderLeftStyle: "solid",
    borderColor: "divider",
  },
  "& [data-swatch='agree']": { borderColor: "success.main" },
  "& [data-swatch='differ']": { borderColor: "warning.main" },
  "& [data-swatch='gap']": { borderLeftStyle: "dashed" },
} as const;
